import React, { useEffect } from 'react';
import type { FallbackProps } from 'react-error-boundary';
import { ErrorBoundary } from 'react-error-boundary';
import { useQueryErrorResetBoundary } from 'react-query';

import { HttpError } from '~/api/client';
import { OAIApiError } from '~/libs/openApiError';

type Props = {
  children: React.ReactNode;
  fallback?:
    | React.ReactNode
    | ((props: QueryErrorFallbackProps) => React.ReactNode);
};

export type QueryErrorFallbackProps = FallbackProps & {
  httpError?: HttpError;
};

/**
 * 에러가 401 Unauthorized인지 확인
 */
function isUnauthorizedError(error: unknown): boolean {
  if (error instanceof HttpError) {
    return error.status === 401;
  }
  if (error instanceof OAIApiError) {
    return error.statusCode === 401;
  }
  return false;
}

/**
 * 401 에러 시 /401 페이지로 리다이렉트
 */
function UnauthorizedRedirect() {
  useEffect(() => {
    window.location.href = '/401';
  }, []);

  return null;
}

function DefaultErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  const httpError = error instanceof HttpError ? error : undefined;

  return (
    <div className="flex flex-col items-center justify-center p-24">
      <p className="mb-16">
        {httpError?.userMessage || '오류가 발생했습니다.'}
      </p>
      <button type="button" onClick={resetErrorBoundary} className="">
        다시 시도
      </button>
    </div>
  );
}

export function QueryErrorBoundary({ children, fallback }: Props) {
  const { reset } = useQueryErrorResetBoundary();

  const renderFallback = ({
    error,
    resetErrorBoundary,
  }: FallbackProps): React.ReactNode => {
    // 401 에러는 항상 /401 페이지로 리다이렉트
    if (isUnauthorizedError(error)) {
      return <UnauthorizedRedirect />;
    }

    const httpError = error instanceof HttpError ? error : undefined;
    const enhancedProps: QueryErrorFallbackProps = {
      error,
      resetErrorBoundary,
      httpError,
    };

    if (typeof fallback === 'function') {
      return fallback(enhancedProps);
    }

    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <DefaultErrorFallback
        error={error}
        resetErrorBoundary={resetErrorBoundary}
      />
    );
  };

  return (
    <ErrorBoundary onReset={reset} fallbackRender={renderFallback}>
      {children}
    </ErrorBoundary>
  );
}
